import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";

/** tiny format helpers kept local */
const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
};

export default function useDashboard() {
  const { api, api2, baseUrl, adminId, latitude, longitude } =
    useContext(GlobalContext);

  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const setSpin = (id, action) => setBusy((p) => ({ ...p, [id]: action }));
  const clearSpin = (id) => setBusy((p) => ({ ...p, [id]: null }))

  const generateWhatsappQRCode = async ({
    userid,
    whatsappNumber,
    message,
  }) => {
    try {
      const { data } = await api.post(
        "/Whatsapp/v2.0/generateWhatsappQRCode",
        {
          userid,
          whatsappNumber,
          message: message || "Hi",
        }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          qrUrl: data?.response?.data || "",
        };
      }

      return {
        success: false,
        message: data?.responseMessage || "Failed to generate QR",
      };
    } catch (error) {
      console.error("generateWhatsappQRCode error:", error);

      return {
        success: false,
        message: "Failed to generate QR",
      };
    }
  };
  const fetchAxisBankBalance = async () => {
    try {
      const { data } = await api.get(
        "https://payin.remittpe.com/api/Payout/AxisBank/GetBalance"
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          balance: Number(data.data.balance),
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Unable to fetch payout balance",
      };
    } catch (error) {
      console.error("Axis payout balance error:", error);

      return {
        success: false,
        message: "Unable to fetch payout balance",
      };
    }
  };

  const getAllOrders = async ({ adminId, limit = 10, offset = 0 } = {}) => {
    if (!adminId) {
      return {
        success: false,
        orders: [],
        message: "Admin ID is required",
      };
    }

    try {
      const payload = {
        adminId: String(adminId),
        limit: Number(limit),
        offset: Number(offset),
      };

      const { data } = await api.post("/Order/v3.0/getAllOrderData", payload);

      if (data?.responseCode === "00") {
        return {
          success: true,
          orders: Array.isArray(data?.response) ? data.response : [],
          message: data?.responseMessage || "Orders fetched successfully",
        };
      }

      return {
        success: false,
        orders: [],
        message: data?.responseMessage || "Failed to fetch orders",
      };
    } catch (error) {
      console.error("getAllOrders error:", error);

      return {
        success: false,
        orders: [],
        message:
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Failed to fetch orders",
      };
    }
  };

  const getOrderAnalytics = async (userId) => {
    try {
      const { data } = await api.post(
        "/Order/v1.0/getOrderAnalytics",
        {
          userid: String(userId),
        }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          analytics: data.response || {},
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to fetch analytics",
      };
    } catch (error) {
      console.error("getOrderAnalytics error:", error);

      return {
        success: false,
        message: "Failed to fetch analytics",
      };
    }
  };
const getClientOrderSummary = async ({ adminId, date, locationId } = {}) => {
  try {
    const payload = {
      adnminId: String(adminId ?? ""), // keep backend key as-is
      date: date || "",
    };

    // only add location_id when a real value is selected
    if (locationId !== undefined && locationId !== null && String(locationId).trim() !== "") {
      payload.location_id = String(locationId);
    }

    const { data } = await api.post(
      "/Admin/v1.0/getClientOrderSummaryData",
      payload
    );

    if (Array.isArray(data?.response)) {
      return {
        success: true,
        rows: data.response,
      };
    }

    return {
      success: true,
      rows: [],
    };
  } catch (error) {
    console.error("getClientOrderSummary error:", error);
    return {
      success: false,
      message: "Failed to fetch Order Summary.",
    };
  }
};
  const updateOrderPaymentStatus = async (orderid) => {
    try {
      const { data } = await api.post(
        "/Order/v1.0/updateOrderPaymentStatus",
        { orderid }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          data: data.response,
        };
      }

      return {
        success: false,
        message: data?.responseMessage || "Update failed",
      };
    } catch (error) {
      console.error("updateOrderPaymentStatus error:", error);

      return {
        success: false,
        message: "Error updating payment",
      };
    }
  };
  const generateOrderPaymentIntent = async (payload) => {
    try {
      const { data } = await api.post(
        "/Order/generatePaymentIntent",
        payload
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Link sent",
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Error generating payment link",
      };
    } catch (error) {
      console.error("generatePaymentIntent error:", error);

      return {
        success: false,
        message: "Error generating payment link",
      };
    }
  };
  const assignDeliveryPartnerV2 = async (payload) => {
    try {
      const { data } = await api.post(
        "/Order/v1.0/assignDeliveryPartner",
        payload
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: "Order sent to delivery partner",
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to send order",
      };
    } catch (error) {
      console.error("assignDeliveryPartnerV2 error:", error);

      return {
        success: false,
        message: "Error forwarding order",
      };
    }
  };
  const processRefund = async ({ userid, amount, transactionId }) => {
    try {
      const { data } = await api.post(
        "/Transactions/v1.0/refundProccess",
        {
          userid,
          amount,
          transactionId,
        }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: data?.responseMessage || "Refund initiated",
        };
      }

      return {
        success: false,
        message: data?.responseMessage || "Refund failed",
      };
    } catch (error) {
      console.error("processRefund error:", error);

      return {
        success: false,
        message: "Error processing refund",
      };
    }
  };

  const requestCustomerAddress = async (orderid) => {
    try {
      const { data } = await api.post(
        "/Whatsapp/v1.0/locationRequest",
        { orderid }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          message: "Address request sent",
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to send address request",
      };
    } catch (error) {
      console.error("requestCustomerAddress error:", error);

      return {
        success: false,
        message: "Error sending address request",
      };
    }
  };
  const getDashboardAnalytics = async ({ adminId, interval = "30" }) => {
    try {
      const { data } = await api.post("/Admin/getDashboardAnalytics", {
        adminId,
        userid: "string",
        interval,
      });

      if (data?.responseCode === "00") {
        return {
          success: true,
          analytics: data.response || {},
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to fetch dashboard analytics",
      };
    } catch (error) {
      console.error("getDashboardAnalytics error:", error);

      return {
        success: false,
        message: "Failed to fetch dashboard analytics",
      };
    }
  };
  const getOrdersDashboard = async ({ adminId, interval = 14 }) => {
    try {
      const { data } = await api.post(
        "/Admin/v1.0/getOrdersDashboard",
        {
          interval,
          adminId,
        }
      );

      if (data?.responseCode === "00") {
        return {
          success: true,
          dashboard: data.response || {},
        };
      }

      return {
        success: false,
        message:
          data?.responseMessage || "Failed to fetch orders dashboard",
      };
    } catch (error) {
      // console.error("getOrdersDashboard error:", error);

      return {
        success: false,
        message: "Failed to fetch orders dashboard",
      };
    }
  };

  return {
    clients,
    orders,
    loading,
    busy,
    generateWhatsappQRCode,
    fetchAxisBankBalance,
    getAllOrders,
    getOrderAnalytics,
    getClientOrderSummary,
    updateOrderPaymentStatus,
    generateOrderPaymentIntent,
    assignDeliveryPartnerV2,
    processRefund,
    requestCustomerAddress,
    getDashboardAnalytics,
    getOrdersDashboard
  }
}